/**
 * Right panel - Inspector and controls
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Box, ScrollArea, Text, Stack, Group, Divider, Button, Slider, Checkbox, Switch, NumberInput, TextInput, Tooltip, Select } from "@mantine/core";
import {
  useViewerStore,
  useSelectedModuleInstanceId,
  useSelectedModuleName,
  useSelectedModulePosition,
  useSelectedModuleRootLink,
  useSelectedModuleCanBeChild,
  useSelectedModulePrefix,
  useAvailableConnections,
  useInstances,
  useEditLinksMode,
  useEditLinksInstanceId,
  useEditLinksModuleId,
  useEditLinksData,
  useEditLinksCanBeChild,
  useEditLinksHasServo,
  useEditLinksEndEffectorMode,
  useEditLinksEndEffectorLink,
  useEditLinksEndEffectorOffsetXyz,
  useEditLinksEndEffectorOffsetRpy,
  useEditLinksCanEditEndEffector,
} from "../store";
import { useWebSocket } from "../WebSocket";
import { recordConnect } from "../hooks";
import { InstanceTree } from "../components/InstanceTree";
import { IKControls } from "./right/IKControls";
import { buildSliderMarks, formatDisplayValue, getJointUnit, snapDisplayValue } from "./right/jointUnits";

const RIGHT_PANEL_DEFAULT_WIDTH = 300;
const RIGHT_PANEL_MIN_WIDTH = 240;
const VIEWER_MIN_WIDTH = 360;
const RIGHT_PANEL_WIDTH_STORAGE_KEY = "robot-compiler.viewer.right-panel-width";

function ModuleInspector() {
  const instanceId = useSelectedModuleInstanceId();
  const moduleName = useSelectedModuleName();
  const position = useSelectedModulePosition();
  const rootLink = useSelectedModuleRootLink();
  const canBeChild = useSelectedModuleCanBeChild();
  const currentPrefix = useSelectedModulePrefix();
  const availableConnections = useAvailableConnections();
  const parentInstanceId = useViewerStore((state) => state.selectedModuleParentId);
  const parentLinkName = useViewerStore((state) => state.selectedModuleParentLink);
  const childLinkName = useViewerStore((state) => state.selectedModuleChildLink);
  const instances = useInstances();
  const { sendPreviewConnection, sendConnectModules, sendDeleteInstance, sendDisconnectInstance, sendEnterEditLinksMode, sendSetInstancePrefix } = useWebSocket();

  const [showDetails, setShowDetails] = useState(false);
  const [yaw, setYaw] = useState<number>(0);
  const [pitch, setPitch] = useState<number>(0);
  const [roll, setRoll] = useState<number>(0);
  const [offset, setOffset] = useState<[number, number, number]>([0, 0, 0]);
  const [prefixInput, setPrefixInput] = useState<string>("");
  const rotationUnit = getJointUnit("revolute");
  const rotationMarks = buildSliderMarks(rotationUnit, -Math.PI, Math.PI);

  // Check if module is connected
  const isConnected = parentInstanceId !== null;
  const parentInstance = isConnected
    ? instances.find((inst) => inst.instance_id === parentInstanceId)
    : null;

  // Child link for connection is always the root_link (base_link)
  // This is the module's input connection point to attach to a parent
  const childConnectionLink = rootLink?.name ?? null;

  // Reset state when instance changes
  useEffect(() => {
    setYaw(0);
    setPitch(0);
    setRoll(0);
    setOffset([0, 0, 0]);
    setShowDetails(false);
    // Set prefix input to current prefix or empty string
    setPrefixInput(currentPrefix ?? "");
  }, [instanceId, currentPrefix]);

  // Handle hover preview
  const handleConnectionHover = useCallback(
    (connectionValue: string | null, childLink: string) => {
      if (!instanceId || !childLink) return;

      if (connectionValue) {
        const [parentInstId, parentLink] = connectionValue.split(":");
        sendPreviewConnection(instanceId, childLink, parentInstId, parentLink, true, 0);
      } else {
        sendPreviewConnection(instanceId, "", "", "", false, 0);
      }
    },
    [instanceId, sendPreviewConnection]
  );

  // Handle click to connect immediately
  const handleConnectionClick = useCallback(
    (connectionValue: string, childLink: string) => {
      if (!instanceId || !childLink) return;

      const [parentInstId, parentLink] = connectionValue.split(":");
      recordConnect(instanceId);
      sendConnectModules(instanceId, childLink, parentInstId, parentLink, 0);
      sendPreviewConnection(instanceId, "", "", "", false, 0);
    },
    [instanceId, sendConnectModules, sendPreviewConnection]
  );

  // Send preview with current transform values
  const sendTransformPreview = useCallback(
    (newYaw: number, newPitch: number, newRoll: number, newOffset: [number, number, number]) => {
      if (instanceId && childLinkName && parentInstanceId && parentLinkName) {
        sendPreviewConnection(
          instanceId, childLinkName, parentInstanceId, parentLinkName,
          true, newYaw, newPitch, newRoll, newOffset
        );
      }
    },
    [instanceId, childLinkName, parentInstanceId, parentLinkName, sendPreviewConnection]
  );

  // Commit transform change
  const commitTransform = useCallback(
    (newYaw: number, newPitch: number, newRoll: number, newOffset: [number, number, number]) => {
      if (instanceId && childLinkName && parentInstanceId && parentLinkName) {
        // Transform adjust — no undo recording here as it's a continuous operation
        sendConnectModules(
          instanceId, childLinkName, parentInstanceId, parentLinkName,
          newYaw, newPitch, newRoll, newOffset
        );
        sendPreviewConnection(instanceId, "", "", "", false, 0, 0, 0, [0, 0, 0]);
      }
    },
    [instanceId, childLinkName, parentInstanceId, parentLinkName, sendConnectModules, sendPreviewConnection]
  );

  // Handle yaw change for connected module
  const handleYawChange = useCallback(
    (newYawDisplay: number) => {
      const snapped = snapDisplayValue(newYawDisplay, rotationUnit, -Math.PI, Math.PI);
      const newYaw = rotationUnit.toNative(snapped);
      setYaw(newYaw);
      sendTransformPreview(newYaw, pitch, roll, offset);
    },
    [pitch, roll, offset, rotationUnit, sendTransformPreview]
  );

  const handleYawChangeEnd = useCallback(
    (newYawDisplay: number) => {
      const snapped = snapDisplayValue(newYawDisplay, rotationUnit, -Math.PI, Math.PI);
      const newYaw = rotationUnit.toNative(snapped);
      commitTransform(newYaw, pitch, roll, offset);
    },
    [pitch, roll, offset, rotationUnit, commitTransform]
  );

  // Handle pitch change
  const handlePitchChange = useCallback(
    (newPitchDisplay: number) => {
      const snapped = snapDisplayValue(newPitchDisplay, rotationUnit, -Math.PI, Math.PI);
      const newPitch = rotationUnit.toNative(snapped);
      setPitch(newPitch);
      sendTransformPreview(yaw, newPitch, roll, offset);
    },
    [yaw, roll, offset, rotationUnit, sendTransformPreview]
  );

  const handlePitchChangeEnd = useCallback(
    (newPitchDisplay: number) => {
      const snapped = snapDisplayValue(newPitchDisplay, rotationUnit, -Math.PI, Math.PI);
      const newPitch = rotationUnit.toNative(snapped);
      commitTransform(yaw, newPitch, roll, offset);
    },
    [yaw, roll, offset, rotationUnit, commitTransform]
  );

  // Handle roll change
  const handleRollChange = useCallback(
    (newRollDisplay: number) => {
      const snapped = snapDisplayValue(newRollDisplay, rotationUnit, -Math.PI, Math.PI);
      const newRoll = rotationUnit.toNative(snapped);
      setRoll(newRoll);
      sendTransformPreview(yaw, pitch, newRoll, offset);
    },
    [yaw, pitch, offset, rotationUnit, sendTransformPreview]
  );

  const handleRollChangeEnd = useCallback(
    (newRollDisplay: number) => {
      const snapped = snapDisplayValue(newRollDisplay, rotationUnit, -Math.PI, Math.PI);
      const newRoll = rotationUnit.toNative(snapped);
      commitTransform(yaw, pitch, newRoll, offset);
    },
    [yaw, pitch, offset, rotationUnit, commitTransform]
  );

  // Handle offset change
  const handleOffsetChange = useCallback(
    (axis: 0 | 1 | 2, value: number) => {
      const newOffset: [number, number, number] = [...offset];
      newOffset[axis] = value;
      setOffset(newOffset);
      sendTransformPreview(yaw, pitch, roll, newOffset);
    },
    [yaw, pitch, roll, offset, sendTransformPreview]
  );

  const handleOffsetChangeEnd = useCallback(
    () => {
      commitTransform(yaw, pitch, roll, offset);
    },
    [yaw, pitch, roll, offset, commitTransform]
  );

  const handleDelete = () => {
    if (instanceId) {
      sendDeleteInstance(instanceId);
    }
  };

  const handleDisconnect = () => {
    if (instanceId) {
      sendDisconnectInstance(instanceId);
    }
  };

  const handleEditLinks = () => {
    if (instanceId) {
      sendEnterEditLinksMode(instanceId);
    }
  };

  // Handle prefix update
  const handlePrefixChange = (value: string) => {
    setPrefixInput(value);
  };

  const handlePrefixBlur = () => {
    if (instanceId && prefixInput !== (currentPrefix ?? "")) {
      sendSetInstancePrefix(instanceId, prefixInput);
    }
  };

  const handlePrefixKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePrefixBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  if (!instanceId) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        Click a module in the scene to inspect
      </Text>
    );
  }

  // Format connection options (parent modules' leaf links that can receive this module)
  const connectionOptions = availableConnections.map((conn) => ({
    value: `${conn.instance_id}:${conn.link_name}`,
    label: `${conn.module_name} / ${conn.link_name}`,
  }));

  return (
    <Stack gap="md">
      {/* Header with module name and buttons */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Module</Text>
          <Text size="sm" fw={600}>{moduleName}</Text>
        </Box>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            onClick={handleEditLinks}
          >
            Edit Links
          </Button>
          <Button
            size="xs"
            color="red"
            variant="light"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Group>
      </Group>

      {/* Prefix input for joint/link naming */}
      <Box>
        <Text size="xs" c="dimmed" mb={4}>Prefix</Text>
        <Group gap="xs" align="flex-start">
          <TextInput
            size="xs"
            value={prefixInput}
            onChange={(e) => handlePrefixChange(e.currentTarget.value)}
            onBlur={handlePrefixBlur}
            onKeyDown={handlePrefixKeyDown}
            placeholder={instanceId ?? ""}
            description="Used for joint/link names in URDF export"
            style={{ flex: 1 }}
            disabled={currentPrefix === ""}
          />
          <Tooltip label={currentPrefix === "" ? "Prefix is disabled" : "Disable prefix (use original joint/link names)"}>
            <Button
              size="xs"
              variant={currentPrefix === "" ? "filled" : "light"}
              color={currentPrefix === "" ? "blue" : "gray"}
              onClick={() => {
                if (instanceId) {
                  if (currentPrefix === "") {
                    // Re-enable prefix (revert to instance_id)
                    sendSetInstancePrefix(instanceId, null);
                  } else {
                    // Disable prefix
                    sendSetInstancePrefix(instanceId, "");
                  }
                }
              }}
            >
              No Prefix
            </Button>
          </Tooltip>
        </Group>
      </Box>

      {/* Position display */}
      {position && (
        <Box>
          <Text size="xs" c="dimmed" mb={4}>Position</Text>
          <Text size="sm" style={{ fontFamily: "monospace" }}>
            [{position[0].toFixed(3)}, {position[1].toFixed(3)}, {position[2].toFixed(3)}]
          </Text>
        </Box>
      )}

      {/* Connected module info & Transform */}
      {isConnected && parentInstance && (
        <>
          <Box>
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Connected To</Text>
                <Text size="sm">
                  {parentInstance.module_name} / {parentLinkName}
                </Text>
                <Text size="xs" c="dimmed">
                  via {childLinkName}
                </Text>
              </Box>
              <Button
                size="xs"
                color="orange"
                variant="light"
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </Group>
          </Box>

          <Divider label="Transform" labelPosition="center" />

          <Group justify="flex-end">
            <Checkbox
              label="Details"
              size="xs"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.currentTarget.checked)}
            />
          </Group>

          {/* Yaw slider */}
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Yaw (deg)</Text>
            <Slider
              value={rotationUnit.toDisplay(yaw)}
              onChange={handleYawChange}
              onChangeEnd={handleYawChangeEnd}
              min={-180}
              max={180}
              step={rotationUnit.step}
              marks={rotationMarks}
              label={(value) => formatDisplayValue(value, rotationUnit)}
              size="sm"
            />
          </Box>

          {showDetails && (
            <>
              {/* Pitch slider */}
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Pitch (deg)</Text>
                <Slider
                  value={rotationUnit.toDisplay(pitch)}
                  onChange={handlePitchChange}
                  onChangeEnd={handlePitchChangeEnd}
                  min={-180}
                  max={180}
                  step={rotationUnit.step}
                  marks={rotationMarks}
                  label={(value) => formatDisplayValue(value, rotationUnit)}
                  size="sm"
                />
              </Box>

              {/* Roll slider */}
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Roll (deg)</Text>
                <Slider
                  value={rotationUnit.toDisplay(roll)}
                  onChange={handleRollChange}
                  onChangeEnd={handleRollChangeEnd}
                  min={-180}
                  max={180}
                  step={rotationUnit.step}
                  marks={rotationMarks}
                  label={(value) => formatDisplayValue(value, rotationUnit)}
                  size="sm"
                />
              </Box>

              <Divider my="xs" label="Offset (m)" labelPosition="center" />

              {/* Offset inputs */}
              <Group grow>
                <NumberInput
                  label="X"
                  value={offset[0]}
                  onChange={(val) => handleOffsetChange(0, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Y"
                  value={offset[1]}
                  onChange={(val) => handleOffsetChange(1, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
                <NumberInput
                  label="Z"
                  value={offset[2]}
                  onChange={(val) => handleOffsetChange(2, typeof val === 'number' ? val : 0)}
                  onBlur={handleOffsetChangeEnd}
                  size="xs"
                  step={0.01}
                  decimalScale={3}
                  hideControls
                />
              </Group>
            </>
          )}
        </>
      )}

      {/* Connection section for unconnected modules */}
      {/* Connection section for unconnected modules with a root_link and can_be_child=true */}
      {!isConnected && childConnectionLink && canBeChild && (
        <>
          <Divider label="Connect" labelPosition="center" />

          {/* Show the connection point (root_link / base_link) */}
          <Text size="xs" c="dimmed">
            Using: {childConnectionLink}
          </Text>

          {/* Parent connection candidates (other modules' leaf links) */}
          {connectionOptions.length > 0 && (
            <Box>
              <Text size="xs" c="dimmed" mb={4}>Connect to:</Text>
              <Stack gap={4}>
                {connectionOptions.map((opt) => (
                  <Box
                    key={opt.value}
                    p="xs"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: 4,
                      cursor: "pointer",
                      transition: "background-color 0.1s",
                    }}
                    onMouseEnter={() => handleConnectionHover(opt.value, childConnectionLink)}
                    onMouseLeave={() => handleConnectionHover(null, childConnectionLink)}
                    onClick={() => handleConnectionClick(opt.value, childConnectionLink)}
                  >
                    <Text size="xs">{opt.label}</Text>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {connectionOptions.length === 0 && (
            <Text size="xs" c="dimmed" ta="center">
              No available connection points on other modules
            </Text>
          )}
        </>
      )}

      {/* No root_link or can_be_child=false means no connection point */}
      {!isConnected && (!childConnectionLink || !canBeChild) && (
        <Text size="xs" c="dimmed" ta="center">
          {!canBeChild
            ? "This module cannot connect to a parent (root-only module)"
            : "This module has no connection point"}
        </Text>
      )}
    </Stack>
  );
}

function EditLinksPanel() {
  const editLinksMode = useEditLinksMode();
  const editLinksInstanceId = useEditLinksInstanceId();
  const editLinksModuleId = useEditLinksModuleId();
  const editLinksData = useEditLinksData();
  const editLinksCanBeChild = useEditLinksCanBeChild();
  const editLinksHasServo = useEditLinksHasServo();
  const editLinksEndEffectorMode = useEditLinksEndEffectorMode();
  const editLinksEndEffectorLink = useEditLinksEndEffectorLink();
  const editLinksEndEffectorOffsetXyz = useEditLinksEndEffectorOffsetXyz();
  const editLinksEndEffectorOffsetRpy = useEditLinksEndEffectorOffsetRpy();
  const editLinksCanEditEndEffector = useEditLinksCanEditEndEffector();
  const toggleLinkConnectable = useViewerStore((state) => state.toggleLinkConnectable);
  const setEditLinksCanBeChild = useViewerStore((state) => state.setEditLinksCanBeChild);
  const setEditLinksHasServo = useViewerStore((state) => state.setEditLinksHasServo);
  const setEditLinksEndEffectorMode = useViewerStore((state) => state.setEditLinksEndEffectorMode);
  const setEditLinksEndEffectorLink = useViewerStore((state) => state.setEditLinksEndEffectorLink);
  const setEditLinksEndEffectorOffsetXyz = useViewerStore((state) => state.setEditLinksEndEffectorOffsetXyz);
  const setEditLinksEndEffectorOffsetRpy = useViewerStore((state) => state.setEditLinksEndEffectorOffsetRpy);
  const exitEditLinksMode = useViewerStore((state) => state.exitEditLinksMode);
  const { sendExitEditLinksMode, sendSaveConnectableLinks, sendShowEndCoordsGizmo } = useWebSocket();
  const rotationUnit = getJointUnit("revolute");
  const isCustomEndEffector = editLinksEndEffectorMode === "custom" && !!editLinksEndEffectorLink;

  const hideEndEffectorGizmo = useCallback(() => {
    sendShowEndCoordsGizmo(false, "", [0, 0, 0], [0, 0, 0]);
  }, [sendShowEndCoordsGizmo]);

  const showEndEffectorGizmo = useCallback(
    (
      linkName: string,
      position: [number, number, number],
      rotation: [number, number, number]
    ) => {
      if (!editLinksInstanceId) {
        return;
      }
      sendShowEndCoordsGizmo(true, `${editLinksInstanceId}:${linkName}`, position, rotation);
    },
    [editLinksInstanceId, sendShowEndCoordsGizmo]
  );

  useEffect(() => {
    if (!editLinksMode || !isCustomEndEffector) {
      hideEndEffectorGizmo();
      return;
    }
    showEndEffectorGizmo(
      editLinksEndEffectorLink as string,
      editLinksEndEffectorOffsetXyz,
      editLinksEndEffectorOffsetRpy
    );
  }, [
    editLinksMode,
    isCustomEndEffector,
    editLinksEndEffectorLink,
    hideEndEffectorGizmo,
    showEndEffectorGizmo,
  ]);

  const handleSave = () => {
    if (editLinksModuleId) {
      const connectableLinks = editLinksData
        .filter((link) => link.is_connectable)
        .map((link) => link.name);
      sendSaveConnectableLinks(
        editLinksModuleId,
        connectableLinks,
        editLinksCanBeChild,
        editLinksHasServo,
        editLinksEndEffectorMode,
        editLinksEndEffectorLink,
        editLinksEndEffectorOffsetXyz,
        editLinksEndEffectorOffsetRpy,
      );
    }
    hideEndEffectorGizmo();
    sendExitEditLinksMode();
    exitEditLinksMode();
  };

  const handleCancel = () => {
    hideEndEffectorGizmo();
    sendExitEditLinksMode();
    exitEditLinksMode();
  };

  if (!editLinksMode) {
    return null;
  }

  const endEffectorOptions = [
    { value: "__auto__", label: "Auto - a gizmo per leaf link" },
    { value: "__none__", label: "None - no gizmos" },
    ...editLinksData.map((link) => ({ value: link.name, label: link.name })),
  ];
  const selectedEndEffectorValue =
    editLinksEndEffectorMode === "custom" && editLinksEndEffectorLink
      ? editLinksEndEffectorLink
      : editLinksEndEffectorMode === "none"
        ? "__none__"
        : "__auto__";

  const handleEndEffectorOffsetXyzChange = (axis: 0 | 1 | 2, value: number | string) => {
    const next: [number, number, number] = [...editLinksEndEffectorOffsetXyz];
    next[axis] = typeof value === "number" ? value : 0;
    setEditLinksEndEffectorOffsetXyz(next);
    if (isCustomEndEffector) {
      showEndEffectorGizmo(editLinksEndEffectorLink as string, next, editLinksEndEffectorOffsetRpy);
    }
  };

  const handleEndEffectorOffsetRpyChange = (axis: 0 | 1 | 2, value: number | string) => {
    const next: [number, number, number] = [...editLinksEndEffectorOffsetRpy];
    const display = typeof value === "number" ? value : 0;
    next[axis] = rotationUnit.toNative(display);
    setEditLinksEndEffectorOffsetRpy(next);
    if (isCustomEndEffector) {
      showEndEffectorGizmo(editLinksEndEffectorLink as string, editLinksEndEffectorOffsetXyz, next);
    }
  };

  return (
    <Box
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "var(--mantine-color-body)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Edit Connectable Links
        </Text>
        <Text size="xs" c="dimmed">
          {editLinksModuleId}
        </Text>
      </Box>

      <ScrollArea style={{ flex: 1 }} p="sm">
        <Stack gap="md">
          {/* Can Be Child Toggle */}
          <Box>
            <Switch
              label="Can be child"
              description="Allow this module to connect to a parent module. Disable for root-only modules (e.g., base modules)."
              checked={editLinksCanBeChild}
              onChange={(e) => setEditLinksCanBeChild(e.currentTarget.checked)}
              size="sm"
            />
          </Box>

          {/* Has Servo Toggle */}
          <Box>
            <Switch
              label="Has servo"
              description="Module contains servo motor(s). Modules without servos don't contribute to servo mapping."
              checked={editLinksHasServo}
              onChange={(e) => setEditLinksHasServo(e.currentTarget.checked)}
              size="sm"
            />
          </Box>

          {editLinksCanEditEndEffector && (
            <>
              <Divider />

              <Box>
                <Text size="xs" c="dimmed" mb="xs">
                  Module end effector (for IK)
                </Text>
                <Stack gap="xs">
                  <Select
                    label="Link"
                    data={endEffectorOptions}
                    value={selectedEndEffectorValue}
                    onChange={(value) => {
                      if (!value || value === "__auto__") {
                        setEditLinksEndEffectorMode("auto");
                        setEditLinksEndEffectorLink(null);
                        hideEndEffectorGizmo();
                        return;
                      }
                      if (value === "__none__") {
                        setEditLinksEndEffectorMode("none");
                        setEditLinksEndEffectorLink(null);
                        hideEndEffectorGizmo();
                        return;
                      }
                      setEditLinksEndEffectorMode("custom");
                      setEditLinksEndEffectorLink(value);
                      showEndEffectorGizmo(
                        value,
                        editLinksEndEffectorOffsetXyz,
                        editLinksEndEffectorOffsetRpy
                      );
                    }}
                    allowDeselect={false}
                    size="xs"
                  />
                  {isCustomEndEffector && (
                    <>
                      <Text size="xs" c="dimmed">Offset XYZ (m)</Text>
                      <Group grow>
                        <NumberInput
                          label="X"
                          value={editLinksEndEffectorOffsetXyz[0]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(0, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                        <NumberInput
                          label="Y"
                          value={editLinksEndEffectorOffsetXyz[1]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(1, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                        <NumberInput
                          label="Z"
                          value={editLinksEndEffectorOffsetXyz[2]}
                          onChange={(value) => handleEndEffectorOffsetXyzChange(2, value)}
                          size="xs"
                          step={0.001}
                          decimalScale={4}
                          hideControls
                        />
                      </Group>
                      <Text size="xs" c="dimmed">Offset RPY (deg)</Text>
                      <Group grow>
                        <NumberInput
                          label="Roll"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[0])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(0, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                        <NumberInput
                          label="Pitch"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[1])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(1, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                        <NumberInput
                          label="Yaw"
                          value={rotationUnit.toDisplay(editLinksEndEffectorOffsetRpy[2])}
                          onChange={(value) => handleEndEffectorOffsetRpyChange(2, value)}
                          size="xs"
                          step={rotationUnit.step}
                          decimalScale={rotationUnit.decimals}
                          hideControls
                        />
                      </Group>
                    </>
                  )}
                </Stack>
              </Box>
            </>
          )}

          <Divider />

          {/* Connectable Links */}
          <Box>
            <Text size="xs" c="dimmed" mb="xs">
              Select which links can accept child modules:
            </Text>
            <Stack gap="xs">
              {editLinksData.map((link) => (
                <Checkbox
                  key={link.name}
                  label={link.name}
                  checked={link.is_connectable}
                  onChange={() => toggleLinkConnectable(link.name)}
                  size="sm"
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </ScrollArea>

      <Box p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button color="blue" size="sm" onClick={handleSave}>
            Save
          </Button>
        </Group>
      </Box>
    </Box>
  );
}

export function RightPanel() {
  const guiElements = useViewerStore((state) => state.guiElements);
  const selectedModuleInstanceId = useSelectedModuleInstanceId();
  const instances = useInstances();
  const editLinksMode = useEditLinksMode();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelWidth, setPanelWidth] = useState(RIGHT_PANEL_DEFAULT_WIDTH);
  const panelWidthRef = useRef(RIGHT_PANEL_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  const rightPanelElements = Object.values(guiElements).filter(
    (el) => el.panel === "right" && el.container_uuid === "root"
  );

  const hasRightElements = rightPanelElements.length > 0;
  const hasSelectedModule = selectedModuleInstanceId !== null;
  const hasInstances = instances.length > 0;

  const clampPanelWidth = useCallback((requestedWidth: number): number => {
    const panelElement = panelRef.current;
    const containerElement = panelElement?.parentElement;
    const leftPanelElement = panelElement?.previousElementSibling?.previousElementSibling as HTMLElement | null;

    const containerWidth = containerElement?.clientWidth ?? window.innerWidth;
    const leftPanelWidth = leftPanelElement?.getBoundingClientRect().width ?? 0;
    const maxWidth = Math.max(RIGHT_PANEL_MIN_WIDTH, containerWidth - leftPanelWidth - VIEWER_MIN_WIDTH);

    return Math.round(Math.min(maxWidth, Math.max(RIGHT_PANEL_MIN_WIDTH, requestedWidth)));
  }, []);

  useEffect(() => {
    setPanelWidth((currentWidth) => {
      let desiredWidth = currentWidth;
      try {
        const saved = Number(localStorage.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY));
        if (Number.isFinite(saved) && saved > 0) {
          desiredWidth = saved;
        }
      } catch {
        // Ignore storage errors and keep default width.
      }
      return clampPanelWidth(desiredWidth);
    });
  }, [clampPanelWidth]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    const onWindowResize = () => {
      setPanelWidth((currentWidth) => clampPanelWidth(currentWidth));
    };
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [clampPanelWidth]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMouseMove = (event: MouseEvent) => {
      const panelElement = panelRef.current;
      if (!panelElement) {
        return;
      }
      const panelRight = panelElement.getBoundingClientRect().right;
      const requestedWidth = panelRight - event.clientX;
      setPanelWidth(clampPanelWidth(requestedWidth));
    };

    const onMouseUp = () => {
      setIsResizing(false);
      try {
        localStorage.setItem(RIGHT_PANEL_WIDTH_STORAGE_KEY, String(panelWidthRef.current));
      } catch {
        // Ignore storage errors.
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isResizing, clampPanelWidth]);

  const handleResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeReset = useCallback(() => {
    setPanelWidth(clampPanelWidth(RIGHT_PANEL_DEFAULT_WIDTH));
    try {
      localStorage.removeItem(RIGHT_PANEL_WIDTH_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }, [clampPanelWidth]);

  return (
    <Box
      ref={panelRef}
      style={{
        width: panelWidth,
        height: "100%",
        backgroundColor: "var(--mantine-color-body)",
        borderLeft: "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        role="separator"
        aria-label="Resize right panel"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleResizeReset}
        style={{
          position: "absolute",
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          zIndex: 120,
          backgroundColor: isResizing ? "var(--mantine-color-blue-6)" : "transparent",
        }}
      />

      {isResizing && (
        <Box
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 110,
            cursor: "col-resize",
          }}
        />
      )}

      {/* Edit Links Mode Overlay */}
      {editLinksMode && <EditLinksPanel />}
      {/* Module Tree Section */}
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Modules
        </Text>
      </Box>
      <Box
        p="sm"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          maxHeight: hasInstances ? 200 : "auto",
          overflowY: "auto",
        }}
      >
        <InstanceTree />
      </Box>

      {/* Inspector Section */}
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Inspector
        </Text>
      </Box>

      <ScrollArea style={{ flex: 1 }} p="sm">
        <ModuleInspector />

        {hasSelectedModule && hasRightElements && (
          <Divider my="md" label="Controls" labelPosition="center" />
        )}

        {hasRightElements && <RightPanelContent />}

        {/* IK Controls Section */}
        {hasInstances && (
          <>
            <Divider my="md" label="IK Controls" labelPosition="center" />
            <IKControls />
          </>
        )}

      </ScrollArea>
    </Box>
  );
}

function RightPanelContent() {
  const guiElements = useViewerStore((state) => state.guiElements);
  const guiContainerChildren = useViewerStore((state) => state.guiContainerChildren);

  const rootChildren = guiContainerChildren["root"];
  if (!rootChildren) return null;

  const elements = Array.from(rootChildren)
    .map((uuid) => guiElements[uuid])
    .filter((el) => el && el.panel === "right")
    .sort((a, b) => a.order - b.order);

  return (
    <>
      {elements.map((element) => {
        switch (element.type) {
          case "folder":
            return (
              <Box key={element.uuid} mb="md">
                <FolderWithChildren element={element} />
              </Box>
            );
          default:
            return (
              <Box key={element.uuid}>
                <GuiElementInline element={element} />
              </Box>
            );
        }
      })}
    </>
  );
}

import { GuiFolder, GuiButton, GuiSlider, GuiDropdown, GuiCheckbox, GuiText } from "../components";
import type { GuiElement, GuiFolderElement } from "../types";

function FolderWithChildren({ element }: { element: GuiFolderElement }) {
  return <GuiFolder element={element} />;
}

function GuiElementInline({ element }: { element: GuiElement }) {
  switch (element.type) {
    case "button":
      return <GuiButton element={element} />;
    case "slider":
      return <GuiSlider element={element} />;
    case "dropdown":
      return <GuiDropdown element={element} />;
    case "checkbox":
      return <GuiCheckbox element={element} />;
    case "text":
      return <GuiText element={element} />;
    default:
      return null;
  }
}
